import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

/**
 * The distinct field paths of a single topic, resolved by name through the
 * shared-topics/fields schema. Disabled until both an org and a non-empty
 * `topicName` are available, so callers can pass `null` while the user hasn't
 * settled on a topic yet. Cached per `(orgId, topicName)`.
 */
export default function useTopicFields(
  topicName: string | null,
  orgId?: string,
) {
  const { currentOrganization } = useAuth();
  const canonicalOrgId = orgId || currentOrganization?.org_id;
  const { topics: topicService } = useDomainServices();

  return useQuery<string[], Error>({
    queryKey: ["topicFields", canonicalOrgId, topicName],
    queryFn: ({ signal }) => {
      if (canonicalOrgId === undefined || !topicName) {
        return Promise.reject(new Error("Invalid orgId or topicName"));
      }

      return topicService.getTopicFields(topicName, {
        abortSignal: signal,
        resourceOwnerId: canonicalOrgId,
      });
    },
    enabled: canonicalOrgId !== undefined && !!topicName,
    staleTime: 30000,
  });
}
